import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Award, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";

export default function Rewards() {
  const { user, refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState({});

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/rewards"), api.get("/redemptions/me")]);
    setRewards(r.data.filter((x) => x.active));
    setRedemptions(m.data);
  };

  useEffect(() => { load(); }, []);

  const claim = async (reward) => {
    if (user.points < reward.points_cost) {
      toast.error("Points insuffisants");
      return;
    }
    setLoading((l) => ({ ...l, [reward.id]: true }));
    try {
      await api.post(`/redemptions/${reward.id}`);
      toast.success("Demande envoyée. Un administrateur la validera bientôt.");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la demande");
    } finally {
      setLoading((l) => ({ ...l, [reward.id]: false }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="rewards-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="label-eyebrow mb-3">Récompenses</div>
          <h1 className="font-display text-5xl lg:text-6xl mb-3">Échangez vos points</h1>
          <p className="text-[#a3a8a4] max-w-2xl">Sélectionnez une récompense, faites votre demande, et recevez-la lors de votre prochaine visite.</p>
        </div>
        <div className="text-right">
          <div className="label-eyebrow mb-1">Solde</div>
          <div className="font-display text-4xl gold-text" data-testid="rewards-points-balance">{user?.points ?? 0} pts</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 fade-up-delay-1">
        {rewards.length === 0 && <div className="col-span-full text-[#a3a8a4] py-12 text-center">Aucune récompense disponible.</div>}
        {rewards.map((r) => {
          const disabled = !user || user.points < r.points_cost || loading[r.id];
          return (
            <div key={r.id} className="group bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] hover:border-[#d4af37]/50 p-8 transition-colors" data-testid={`reward-${r.id}`}>
              <div className="flex justify-between items-start mb-6">
                <Award className="gold-text" size={32}/>
                <span className="label-eyebrow">{r.points_cost} pts</span>
              </div>
              <div className="font-display text-2xl mb-3">{r.name}</div>
              <p className="text-sm text-[#a3a8a4] mb-8 min-h-[3rem]">{r.description}</p>
              <button
                disabled={disabled}
                onClick={() => claim(r)}
                data-testid={`claim-${r.id}`}
                className="w-full bg-[#d4af37] text-black px-5 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a] transition-colors disabled:bg-[#1a241b] disabled:text-[#a3a8a4] disabled:cursor-not-allowed"
              >
                {loading[r.id] ? "Envoi..." : user && user.points >= r.points_cost ? "Demander" : `Manque ${r.points_cost - (user?.points ?? 0)} pts`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-16 fade-up-delay-2">
        <div className="label-eyebrow mb-2">Mes demandes</div>
        <h2 className="font-display text-3xl mb-6">Statut de vos demandes</h2>
        <div className="border border-[rgba(253,251,247,0.08)]">
          {redemptions.length === 0 && <div className="p-8 text-[#a3a8a4]">Aucune demande pour l'instant.</div>}
          {redemptions.map((r) => (
            <div key={r.id} className="border-b border-[rgba(253,251,247,0.05)] last:border-0 px-6 py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`my-redemption-${r.id}`}>
              <div>
                <div className="text-sm">{r.reward_name}</div>
                <div className="text-xs text-[#a3a8a4]">{new Date(r.created_at).toLocaleString("fr-FR")} · {r.points_cost} pts</div>
              </div>
              <StatusBadge status={r.status}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { Icon: Clock, label: "En attente", color: "text-[#d4af37]" },
    approved: { Icon: Check, label: "Approuvée", color: "text-emerald-400" },
    rejected: { Icon: X, label: "Refusée", color: "text-red-400" },
  };
  const it = map[status] || map.pending;
  const { Icon, label, color } = it;
  return (
    <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${color}`}>
      <Icon size={14}/> {label}
    </span>
  );
}
